// src/services/SessionStorageService.js

/**
 * Service to encapsulate JSON-based sessionStorage operations.
 */
class SessionStorageService {
  /**
   * Save any serializable value under the given key.
   * @param {string} key 
   * @param {*} value 
   */
  static setItem(key, value) {
    try {
      const serialized = JSON.stringify(value);
      sessionStorage.setItem(key, serialized);
    } catch (err) {
      console.error(`Error saving key "${key}" to sessionStorage:`, err);
    }
  }

  /**
   * Read and parse a value from sessionStorage.
   * @param {string} key 
   * @returns {*} Parsed object, or null if not found.
   */
  static getItem(key) {
    try {
      const serialized = sessionStorage.getItem(key);
      return serialized ? JSON.parse(serialized) : null;
    } catch (err) {
      console.error(`Error reading key "${key}" from sessionStorage:`, err);
      return null;
    }
  }

  /**
   * Remove a key from sessionStorage.
   * @param {string} key 
   */
  static removeItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (err) {
      console.error(`Error removing key "${key}" from sessionStorage:`, err);
    }
  }

  //
  // Chat-specific helpers:
  //

  /** Fixed key under which all chat text areas are stored */
  static TEXT_AREAS_KEY = 'chatTextInput';

  /**
   * Get the full map { [chatId]: text }.
   * @returns {Record<string,string>}
   */
  static getTextAreas() {
    return SessionStorageService.getItem(SessionStorageService.TEXT_AREAS_KEY) || {};
  }

  /**
   * Save the full map { [chatId]: text }.
   * @param {Record<string,string>} map 
   */
  static setTextAreas(map) {
    SessionStorageService.setItem(SessionStorageService.TEXT_AREAS_KEY, map);
  }

  /**
   * Get the text for a specific chat.
   * @param {string} chatId 
   * @returns {string} Saved text or empty string.
   */
  static getTextForChat(chatId) {
    const all = SessionStorageService.getTextAreas();
    return all[chatId] || '';
  }

  /**
   * Save only one chat’s text within the map.
   * @param {string} chatId 
   * @param {string} text 
   */
  static setTextForChat(chatId, text) {
    const all = SessionStorageService.getTextAreas();
    all[chatId] = text;
    SessionStorageService.setTextAreas(all);
  }
}

export default SessionStorageService;
